import InputLabel from "designer/ui/smart/input-label/input-label";
import { GlideFC } from "lib/glide-fc";
import { Button } from "designer/ui/smart/button/button-style";
import { styled } from "sharedUI/styles";
import { useCallback, useState } from "react";
import { CapturePromoCodeResult } from "common/firebase-function-types";
import { Owner } from "common/Database";
import { SubscriptionType } from "common/app-plans";
import { assertNever, dontAwait } from "common/Support";
import { applyPromoCode } from "designer/apps/orgs/orgs-lib";
import { DeepReadonly } from "utility-types";

const PromoCodeEntryContainer = styled.div`
    display: flex;
    flex-direction: column;
    padding: 16px 20px 20px 20px;

    > .promo-wrapper {
        height: 36px;
        display: flex;

        input {
            background-color: ${p => p.theme.upgradeModal.promoCode.background};
            border: 1px solid ${p => p.theme.upgradeModal.border};
            margin-right: 8px;
            height: 36px;
        }

        button {
            height: 36px;
        }
    }

    > .promo-warning {
        margin-top: 4px;
        color: ${p => p.theme.errorColor};
        font-size: 10px;
    }
`;

type PromoCodeState =
    | boolean // "false" means not applying, "true" means applying
    | { error: CapturePromoCodeResult; subscriptionSerial: number }; // If error is OK, wait until subscriptionSerial on owner

function promoStateApplied(
    state: PromoCodeState,
    owner: DeepReadonly<Owner>, // REVIEW: NEED TO REVIEW this. I would have had to make the whole heirarchy VPC if I made this component a VPC
    subscriptionType: SubscriptionType,
    promoCodeInput: string
): boolean {
    if (promoCodeInput === "") return false;

    if (state === true) return false;
    if (state === false) return true;

    const { error, subscriptionSerial } = state;
    if (error !== CapturePromoCodeResult.Ok) return true;

    if (subscriptionType === "monthly") {
        return (owner.monthlySubscription?.serial ?? -1) >= subscriptionSerial;
    } else if (subscriptionType === "annual") {
        return (owner.annualSubscription?.serial ?? -1) >= subscriptionSerial;
    } else {
        return assertNever(subscriptionType);
    }
}

// REVIEW: Review removing subscription serial from app-settings-plan-controller ???
interface Props {
    owner: Owner;
    subscription: SubscriptionType;
    onPromoCodeApplied: () => void;
}

export const PromoCodeEntry: GlideFC<Props> = p => {
    const { subscription, owner, onPromoCodeApplied } = p;

    const [promoCodeInput, setPromoCodeInput] = useState<string>("");

    const [promoState, setPromoState] = useState<PromoCodeState>(false);
    const [promoError, setPromoError] = useState<string | undefined>(undefined);

    const canChangeOptions = promoStateApplied(promoState, owner, subscription, promoCodeInput);

    const tryToApplyPromo = useCallback(
        async (promoCode: string) => {
            if (promoCode === "") return;

            setPromoState(true);

            const response = await applyPromoCode({
                ownerID: owner.id,
                promoCode: promoCode.toUpperCase(),
                subscriptionType: subscription,
            });
            if (typeof response === "object" && response !== undefined) {
                setPromoError(undefined);
                setPromoState({ error: CapturePromoCodeResult.Ok, subscriptionSerial: response.ownerSerial });
                onPromoCodeApplied();
                return;
            }
            setPromoState(false);

            if (response === undefined) {
                setPromoError("Something went wrong. Please try again.");
            } else if (response === CapturePromoCodeResult.Ok) {
                setPromoError(undefined);
            } else if (response === CapturePromoCodeResult.InvalidCode) {
                setPromoError("Invalid code");
            } else if (response === CapturePromoCodeResult.ExpiredCode) {
                setPromoError("This code is no longer available.");
            } else if (response === CapturePromoCodeResult.OwnerIneligible) {
                setPromoError("You are not eligible to apply this code.");
            }
        },
        [onPromoCodeApplied, owner.id, subscription]
    );

    const onInputChange = useCallback(
        (e: React.ChangeEvent<HTMLInputElement>) => {
            setPromoError("");
            setPromoCodeInput(e.target.value);
        },
        [setPromoCodeInput]
    );

    const onButtonPress = useCallback(() => {
        setPromoError("");
        dontAwait(tryToApplyPromo(promoCodeInput));
    }, [promoCodeInput, tryToApplyPromo]);

    return (
        <PromoCodeEntryContainer>
            <div className="promo-wrapper">
                <InputLabel
                    label={undefined}
                    value={promoCodeInput}
                    onChange={onInputChange}
                    placeholder="Got a coupon?"
                    margin={0}
                />
                <Button width={75} disabled={!canChangeOptions} variant="filled" onClick={onButtonPress}>
                    Redeem
                </Button>
            </div>
            {promoError !== undefined && <div className="promo-warning">{promoError}</div>}
        </PromoCodeEntryContainer>
    );
};
